import type { Sky } from '@trippath/shared';

/** A day's sky drawn: a sun, a cloud, rain, snow or thunder, coloured by the tokens; its words
 * in `label` when it stands alone. In the dark, a moon where the sun would be. */
export function SkyIcon({
  sky,
  size = 22,
  label,
  night = false,
}: {
  sky: Sky;
  size?: number;
  label?: string;
  /** Before sunrise or after sunset. */
  night?: boolean;
}) {
  const sun = <circle className="sky-sun" cx="12" cy="12" r="4.5" />;
  const rays = (
    <path
      className="sky-rays"
      d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4"
    />
  );
  const cloud = (y: number) => (
    <path
      className="sky-cloud"
      d={`M7 ${String(y)}h11a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.5 2A3.5 3.5 0 0 0 7 ${String(y)}z`}
    />
  );
  const drops = (cls: string) => <path className={cls} d="M9 18l-1 3M13 18l-1 3M17 18l-1 3" />;
  // A crescent: the moon's disc, its right side cut away by a second arc as wide.
  const moon = (x: number, y: number, r: number) => {
    const at = (dx: number, dy: number) =>
      `${(x + dx * r).toFixed(2)} ${(y + dy * r).toFixed(2)}`;
    return (
      <path
        className="sky-moon"
        d={`M${at(0.3, -0.95)}A${String(r)} ${String(r)} 0 1 0 ${at(0.3, 0.95)}A${String(r)} ${String(r)} 0 0 1 ${at(0.3, -0.95)}Z`}
      />
    );
  };
  const drawn = {
    sun: night ? (
      moon(12, 12, 7)
    ) : (
      <>
        {sun}
        {rays}
      </>
    ),
    part: (
      <>
        {night ? moon(9, 9, 5) : <circle className="sky-sun" cx="9" cy="9" r="4" />}
        {cloud(19)}
      </>
    ),
    cloud: cloud(19),
    fog: (
      <>
        {cloud(15)}
        <path className="sky-fog" d="M4 19h16M6 22h12" />
      </>
    ),
    rain: (
      <>
        {cloud(15)}
        {drops('sky-drops')}
      </>
    ),
    snow: (
      <>
        {cloud(15)}
        {drops('sky-flakes')}
      </>
    ),
    storm: (
      <>
        {cloud(15)}
        <path className="sky-bolt" d="M13 15l-3 4h3l-2 4" />
      </>
    ),
  }[sky];
  return (
    <svg
      className="sky-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {drawn}
    </svg>
  );
}
